// Desktop capture for IT tickets: grab a still frame of any window/screen, or
// record it for up to 60 seconds. Both use getDisplayMedia, so the browser's
// own share picker gates every capture — nothing is captured silently.
//
// Captures upload STRAIGHT to Vercel Blob from the browser (via a scoped
// client token from /api/blob-upload-token), so they never travel through the
// ticket-JSON proxy. That removes the old ~4.5 MB body ceiling — quality can
// be high and recordings can be long — and it's lighter on slow machines
// (a streamed upload, not a multi-megabyte base64 string held in memory).

import { uploadToBlob } from './blob-upload';

export const RECORD_MAX_MS = 60_000;

export type CapturedAttachment = { name: string; type: string; size: number; url: string };
export type RecordingHandle = { stop: () => void };

export function captureSupported(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    !!navigator.mediaDevices?.getDisplayMedia &&
    typeof MediaRecorder !== 'undefined'
  );
}

async function getStream(): Promise<MediaStream | null> {
  try {
    return await navigator.mediaDevices.getDisplayMedia({
      // Full detail — resolution drives text legibility, VP9 keeps static
      // screen frames small, and there's no proxy size limit anymore.
      video: { frameRate: { ideal: 15, max: 30 }, width: { max: 2560 }, height: { max: 1600 } },
      audio: false,
      // Chrome: allow picking the current tab too.
      ...({ selfBrowserSurface: 'include', surfaceSwitching: 'include' } as object),
    });
  } catch {
    return null; // picker dismissed / permission denied
  }
}

// Upload a captured File straight to Vercel Blob (see ./blob-upload). The
// caller's OneLogin token is required — /api/blob-upload-token only signs for
// signed-in employees.
export async function uploadCapture(file: File, accessToken: string | undefined): Promise<CapturedAttachment> {
  return uploadToBlob(file, 'it-tickets', accessToken);
}

/** One still frame of whatever the user picks, as a PNG data URL for the
 *  annotator (or null if the picker was dismissed). */
export async function captureScreenshotFrame(): Promise<string | null> {
  const stream = await getStream();
  if (!stream) return null;
  try {
    const video = document.createElement('video');
    video.srcObject = stream;
    video.muted = true;
    await video.play();
    await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx || !canvas.width) return null;
    ctx.drawImage(video, 0, 0);
    return canvas.toDataURL('image/png');
  } catch {
    return null;
  } finally {
    stream.getTracks().forEach((t) => t.stop());
  }
}

function pickMimeType(): string {
  for (const t of ['video/webm;codecs=vp9', 'video/webm;codecs=vp8', 'video/webm', 'video/mp4']) {
    if (MediaRecorder.isTypeSupported(t)) return t;
  }
  return '';
}

/**
 * Record the picked window/screen, hard-capped at 60 s. The browser's own
 * "stop sharing" UI is honored. onDone receives the finished File (ready to
 * upload) or null if nothing was recorded.
 */
export async function startScreenRecording(opts: {
  onTick: (elapsedMs: number) => void;
  onDone: (file: File | null, error?: string) => void;
}): Promise<RecordingHandle | null> {
  const stream = await getStream();
  if (!stream) return null;

  const mimeType = pickMimeType();
  let recorder: MediaRecorder;
  try {
    recorder = new MediaRecorder(stream, {
      ...(mimeType ? { mimeType } : {}),
      // No proxy ceiling now — bitrate tuned for crisp screen text.
      videoBitsPerSecond: 2_500_000,
    });
  } catch {
    stream.getTracks().forEach((t) => t.stop());
    opts.onDone(null, "Recording isn't supported in this browser");
    return null;
  }

  const chunks: Blob[] = [];
  const startedAt = Date.now();
  let finished = false;

  const ticker = setInterval(() => opts.onTick(Date.now() - startedAt), 500);
  const timeCap = setTimeout(() => stop(), RECORD_MAX_MS);

  function cleanup() {
    clearInterval(ticker);
    clearTimeout(timeCap);
    stream!.getTracks().forEach((t) => t.stop());
  }
  function stop() {
    if (recorder.state !== 'inactive') {
      try { recorder.stop(); } catch { /* already stopping */ }
    }
  }

  recorder.ondataavailable = (e) => { if (e.data.size > 0) chunks.push(e.data); };
  recorder.onstop = () => {
    if (finished) return;
    finished = true;
    cleanup();
    const type = (mimeType || 'video/webm').split(';')[0];
    const blob = new Blob(chunks, { type });
    if (blob.size === 0) { opts.onDone(null); return; }
    const ext = type.includes('mp4') ? 'mp4' : 'webm';
    opts.onDone(new File([blob], `screen-recording.${ext}`, { type }));
  };
  recorder.onerror = () => {
    if (finished) return;
    finished = true;
    cleanup();
    opts.onDone(null, 'Recording failed');
  };

  stream.getVideoTracks()[0]?.addEventListener('ended', stop);
  recorder.start(1000);
  return { stop };
}
